import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DndContext,
  DragEndEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { extractVimeoHash, fetchOembedThumbnail } from '@/utils/oembed';
import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { ModalFooter } from '@/components/ui/ModalFooter';
import { Select } from '@/components/ui/Select';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Toggle } from '@/components/ui/Toggle';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { ICONS } from '@/constants/icons';
import { toast } from '@/utils/toast';
import { helpAdminService } from '@/services/help.service';
import type {
  HelpSection,
  HelpVideo,
  HelpVideoProvider,
  HelpVideoType,
  CreateSectionInput,
  UpdateSectionInput,
  CreateVideoInput,
  UpdateVideoInput,
} from '@/types/help';

// ---------------------------------------------------------------------------
// Query key
// ---------------------------------------------------------------------------
const QUERY_KEY = ['admin', 'help', 'sections'] as const;

// ---------------------------------------------------------------------------
// Thumbnail helpers
// ---------------------------------------------------------------------------
function getThumbnailUrl(video: HelpVideo): string | null {
  if (video.thumbnailUrl) return video.thumbnailUrl;
  if (video.provider === 'YOUTUBE' && video.type === 'VIDEO') {
    return `https://img.youtube.com/vi/${video.videoId}/hqdefault.jpg`;
  }
  // Vimeo: vumbnail.com is intentionally skipped — it returns HTTP 200 with a
  // fake "error.jpg" placeholder on lookup failures, so onError never fires.
  // SortableVideoRow handles Vimeo thumbnails via the oEmbed query instead.
  // Playlists (YouTube or Vimeo) have no reliable auto-thumbnail.
  return null;
}

// ---------------------------------------------------------------------------
// Slugify helper
// ---------------------------------------------------------------------------
function slugify(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

// ---------------------------------------------------------------------------
// Section modal
// ---------------------------------------------------------------------------
interface SectionModalProps {
  open: boolean;
  onClose: () => void;
  initialData?: HelpSection | null;
}

const SectionModal = ({ open, onClose, initialData }: SectionModalProps) => {
  const queryClient = useQueryClient();
  const isEdit = !!initialData;

  const [title, setTitle] = useState(initialData?.title ?? '');
  const [slug, setSlug] = useState(initialData?.slug ?? '');
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(isEdit);
  const [description, setDescription] = useState(initialData?.description ?? '');
  const [sortOrder, setSortOrder] = useState<number>(initialData?.sortOrder ?? 0);
  const [isPublished, setIsPublished] = useState(initialData?.isPublished ?? false);

  // Reset when modal opens with new data
  const handleTitleChange = (value: string) => {
    setTitle(value);
    if (!slugManuallyEdited) {
      setSlug(slugify(value));
    }
  };

  const createMutation = useMutation({
    mutationFn: (input: CreateSectionInput) => helpAdminService.createSection(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast.success('Section created');
      onClose();
    },
    onError: (err: unknown) => {
      const message =
        err instanceof Error ? err.message : 'Failed to create section';
      toast.error(message);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (input: UpdateSectionInput) =>
      helpAdminService.updateSection(initialData!.id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast.success('Section updated');
      onClose();
    },
    onError: (err: unknown) => {
      const message =
        err instanceof Error ? err.message : 'Failed to update section';
      toast.error(message);
    },
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    const payload = {
      title: title.trim(),
      slug: slug.trim() || slugify(title.trim()),
      description: description.trim() || undefined,
      sortOrder,
      isPublished,
    };
    if (isEdit) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={(o) => !o && onClose()}
      title={isEdit ? 'Edit Section' : 'Add Section'}
      size="md"
      footer={
        <ModalFooter>
          <Button variant="secondary" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button type="submit" form="section-form" loading={isPending}>
            {isEdit ? 'Save Changes' : 'Create Section'}
          </Button>
        </ModalFooter>
      }
    >
      <form id="section-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Title <span className="text-red-500">*</span>
          </label>
          <Input
            value={title}
            onChange={(e) => handleTitleChange(e.target.value)}
            placeholder="Getting Started"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Slug <span className="text-red-500">*</span>
          </label>
          <Input
            value={slug}
            onChange={(e) => {
              setSlug(e.target.value);
              setSlugManuallyEdited(true);
            }}
            placeholder="getting-started"
            required
          />
          <p className="mt-1 text-xs text-gray-500">
            URL-friendly identifier (auto-generated from title if left blank)
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional description shown on the Help page"
            rows={3}
          />
        </div>

        <div className="flex items-center gap-6">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sort Order
            </label>
            <Input
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(parseInt(e.target.value, 10) || 0)}
              className="w-24"
            />
          </div>
          <div className="flex items-center gap-3 mt-5">
            <Toggle
              label="Published"
              checked={isPublished}
              onChange={setIsPublished}
            />
          </div>
        </div>
      </form>
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// Video modal
// ---------------------------------------------------------------------------
interface VideoModalProps {
  open: boolean;
  onClose: () => void;
  sections: HelpSection[];
  initialData?: HelpVideo | null;
  defaultSectionId?: number;
}

const VideoModal = ({
  open,
  onClose,
  sections,
  initialData,
  defaultSectionId,
}: VideoModalProps) => {
  const queryClient = useQueryClient();
  const isEdit = !!initialData;

  const [sectionId, setSectionId] = useState<number>(
    initialData?.sectionId ?? defaultSectionId ?? (sections[0]?.id ?? 0)
  );
  const [title, setTitle] = useState(initialData?.title ?? '');
  const [description, setDescription] = useState(initialData?.description ?? '');
  const [videoUrl, setVideoUrl] = useState(initialData?.videoUrl ?? '');
  const [thumbnailUrl, setThumbnailUrl] = useState(initialData?.thumbnailUrl ?? '');
  const [sortOrder, setSortOrder] = useState<number>(initialData?.sortOrder ?? 0);
  const [isPublished, setIsPublished] = useState(initialData?.isPublished ?? false);

  const createMutation = useMutation({
    mutationFn: (input: CreateVideoInput) => helpAdminService.createVideo(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast.success('Video added');
      onClose();
    },
    onError: (err: unknown) => {
      const message =
        err instanceof Error ? err.message : 'Failed to add video';
      toast.error(message);
    },
  });

  const updateMutation = useMutation({
    mutationFn: (input: UpdateVideoInput) =>
      helpAdminService.updateVideo(initialData!.id, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast.success('Video updated');
      onClose();
    },
    onError: (err: unknown) => {
      const message =
        err instanceof Error ? err.message : 'Failed to update video';
      toast.error(message);
    },
  });

  const isPending = createMutation.isPending || updateMutation.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !videoUrl.trim() || !sectionId) return;
    const payload = {
      sectionId,
      title: title.trim(),
      description: description.trim() || undefined,
      videoUrl: videoUrl.trim(),
      thumbnailUrl: thumbnailUrl.trim() || undefined,
      sortOrder,
      isPublished,
    };
    if (isEdit) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  };

  const sectionOptions = sections.map((s) => ({
    value: String(s.id),
    label: s.title,
  }));

  return (
    <Modal
      open={open}
      onOpenChange={(o) => !o && onClose()}
      title={isEdit ? 'Edit Video' : 'Add Video'}
      size="md"
      footer={
        <ModalFooter>
          <Button variant="secondary" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button type="submit" form="video-form" loading={isPending}>
            {isEdit ? 'Save Changes' : 'Add Video'}
          </Button>
        </ModalFooter>
      }
    >
      <form id="video-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Section <span className="text-red-500">*</span>
          </label>
          <Select
            value={String(sectionId)}
            onValueChange={(v) => setSectionId(parseInt(v, 10))}
            options={sectionOptions}
            placeholder="Select a section"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Title <span className="text-red-500">*</span>
          </label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="How to connect a channel"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Video URL <span className="text-red-500">*</span>
          </label>
          <Input
            type="url"
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
            placeholder="https://www.youtube.com/watch?v=..."
            required
          />
          <p className="mt-1 text-xs text-gray-500">
            Paste a YouTube or Vimeo URL — provider, video ID, and type are detected automatically.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Short description displayed below the video"
            rows={3}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Custom Thumbnail URL
          </label>
          <Input
            type="url"
            value={thumbnailUrl}
            onChange={(e) => setThumbnailUrl(e.target.value)}
            placeholder="Leave blank to use automatic thumbnail"
          />
          <p className="mt-1 text-xs text-gray-500">
            Optional. Leave blank to use the auto-generated thumbnail from YouTube / Vimeo.
          </p>
        </div>

        <div className="flex items-center gap-6">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Sort Order
            </label>
            <Input
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(parseInt(e.target.value, 10) || 0)}
              className="w-24"
            />
          </div>
          <div className="flex items-center gap-3 mt-5">
            <Toggle
              label="Published"
              checked={isPublished}
              onChange={setIsPublished}
            />
          </div>
        </div>
      </form>
    </Modal>
  );
};

// ---------------------------------------------------------------------------
// Provider badge
// ---------------------------------------------------------------------------
interface ProviderBadgeProps {
  provider: HelpVideoProvider;
  type: HelpVideoType;
}

const ProviderBadge = ({ provider, type }: ProviderBadgeProps) => {
  const isPlaylist = type === 'PLAYLIST';
  const isYouTube = provider === 'YOUTUBE';

  return (
    <div className="flex items-center gap-1">
      <span
        className={`px-1.5 py-0.5 text-[10px] font-semibold rounded ${
          isYouTube
            ? 'bg-red-100 text-red-700'
            : 'bg-blue-100 text-blue-700'
        }`}
      >
        {isYouTube ? 'YouTube' : 'Vimeo'}
      </span>
      {isPlaylist && (
        <span className="px-1.5 py-0.5 text-[10px] font-semibold rounded bg-soft-coral text-secondary-700">
          Playlist
        </span>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Published pill
// ---------------------------------------------------------------------------
const PublishedPill = ({ isPublished }: { isPublished: boolean }) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${
      isPublished
        ? 'bg-green-100 text-green-700'
        : 'bg-gray-100 text-gray-500'
    }`}
  >
    {isPublished ? 'Published' : 'Draft'}
  </span>
);

// ---------------------------------------------------------------------------
// Sortable video row
// ---------------------------------------------------------------------------
interface SortableVideoRowProps {
  video: HelpVideo;
  onEdit: (v: HelpVideo) => void;
  onDelete: (v: HelpVideo) => void;
}

const SortableVideoRow = ({ video, onEdit, onDelete }: SortableVideoRowProps) => {
  const { t } = useTranslation('common');
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: video.id });

  const [imgError, setImgError] = useState(false);
  const thumbnailSrc = getThumbnailUrl(video);

  // Videos without an admin-uploaded thumbnail need an oEmbed lookup:
  //  - All Vimeo content (single videos + showcases).
  //  - YouTube playlists (no static CDN thumbnail available).
  //  - YouTube single videos use the static CDN URL (thumbnailSrc), no oEmbed needed.
  const vimeoHash = extractVimeoHash(video.videoUrl);
  const needsOEmbed =
    !video.thumbnailUrl &&
    (
      (video.provider === 'VIMEO') ||
      (video.provider === 'YOUTUBE' && video.type === 'PLAYLIST')
    );

  const { data: oembedThumb } = useQuery<string | null>({
    queryKey: ['oembed', video.provider, video.type, video.videoId, vimeoHash],
    queryFn: () => fetchOembedThumbnail({
      provider: video.provider,
      type: video.type,
      videoId: video.videoId,
      vimeoHash,
    }),
    enabled: needsOEmbed,
    staleTime: 24 * 60 * 60 * 1000,
    retry: 1,
  });

  const effectiveSrc = thumbnailSrc ?? oembedThumb ?? null;

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-3 px-4 py-3 bg-white border border-gray-100 rounded-lg hover:bg-gray-50 group"
    >
      {/* Drag handle */}
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 shrink-0 touch-none"
        aria-label="Drag to reorder"
        type="button"
      >
        <i className={`hm hm-${ICONS.DRAG} text-sm`} aria-hidden="true" />
      </button>

      {/* Thumbnail */}
      <div className="shrink-0 w-20 h-[45px] rounded overflow-hidden bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
        {effectiveSrc && !imgError ? (
          <img
            src={effectiveSrc}
            alt=""
            className="w-full h-full object-cover"
            onError={() => setImgError(true)}
            loading="lazy"
          />
        ) : (
          <i className={`hm hm-${ICONS.PLAY} text-gray-400 text-xl`} aria-hidden="true" />
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-gray-900 truncate">{video.title}</span>
          <ProviderBadge provider={video.provider} type={video.type} />
          <PublishedPill isPublished={video.isPublished} />
        </div>
        {video.description && (
          <p className="text-xs text-gray-500 truncate mt-0.5">{video.description}</p>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:opacity-100 transition-opacity">
        <button
          onClick={() => onEdit(video)}
          className="inline-flex items-center justify-center rounded-lg p-1.5 [@media(hover:none)]:min-h-[44px] [@media(hover:none)]:min-w-[44px] text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
          aria-label={`${t('edit')}: ${video.title}`}
          type="button"
        >
          <i className={`hm hm-${ICONS.EDIT} text-sm`} aria-hidden="true" />
        </button>
        <button
          onClick={() => onDelete(video)}
          className="inline-flex items-center justify-center rounded-lg p-1.5 [@media(hover:none)]:min-h-[44px] [@media(hover:none)]:min-w-[44px] text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
          aria-label={`${t('delete')}: ${video.title}`}
          type="button"
        >
          <i className={`hm hm-${ICONS.TRASH} text-sm`} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Sortable section card
// ---------------------------------------------------------------------------
interface SortableSectionCardProps {
  section: HelpSection;
  onEditSection: (s: HelpSection) => void;
  onDeleteSection: (s: HelpSection) => void;
  onAddVideo: (s: HelpSection) => void;
  onEditVideo: (v: HelpVideo) => void;
  onDeleteVideo: (v: HelpVideo) => void;
  onVideoDragEnd: (sectionId: number, event: DragEndEvent) => void;
}

const SortableSectionCard = ({
  section,
  onEditSection,
  onDeleteSection,
  onAddVideo,
  onEditVideo,
  onDeleteVideo,
  onVideoDragEnd,
}: SortableSectionCardProps) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id });

  const [menuOpen, setMenuOpen] = useState(false);

  const videoSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const sortedVideos = [...section.videos].sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white rounded-xl border ${isDragging ? 'border-primary-300 shadow-lg' : 'border-gray-200'} overflow-hidden`}
    >
      {/* Section header */}
      <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100 bg-gray-50">
        {/* Drag handle */}
        <button
          {...attributes}
          {...listeners}
          className="cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 shrink-0 touch-none"
          aria-label="Drag section to reorder"
          type="button"
        >
          <i className={`hm hm-${ICONS.DRAG} text-base`} aria-hidden="true" />
        </button>

        {/* Title + slug */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-base sm:text-xl font-semibold text-gray-900">{section.title}</span>
            <span className="text-xs text-gray-400 font-mono">{section.slug}</span>
            <PublishedPill isPublished={section.isPublished} />
          </div>
          {section.description && (
            <p className="text-xs text-gray-500 mt-0.5 truncate">{section.description}</p>
          )}
        </div>

        {/* Add video button */}
        <Button size="sm" onClick={() => onAddVideo(section)}>
          <i className={`hm hm-${ICONS.PLUS} mr-1.5`} aria-hidden="true" />
          Add Video
        </Button>

        {/* Section menu */}
        <div className="relative">
          <button
            onClick={() => setMenuOpen((o) => !o)}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-200 transition-colors"
            aria-label="Section actions"
            aria-haspopup="true"
            aria-expanded={menuOpen}
            type="button"
          >
            <i className={`hm hm-${ICONS.ELLIPSIS_VERTICAL} text-base`} aria-hidden="true" />
          </button>
          {menuOpen && (
            <div
              className="absolute right-0 top-full mt-1 w-36 bg-white rounded-lg border border-gray-200 shadow-lg z-10 py-1"
              onMouseLeave={() => setMenuOpen(false)}
            >
              <button
                onClick={() => { setMenuOpen(false); onEditSection(section); }}
                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                type="button"
              >
                <i className={`hm hm-${ICONS.EDIT} text-sm`} aria-hidden="true" />
                Edit
              </button>
              <button
                onClick={() => { setMenuOpen(false); onDeleteSection(section); }}
                className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                type="button"
              >
                <i className={`hm hm-${ICONS.TRASH} text-sm`} aria-hidden="true" />
                Delete
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Videos list */}
      <div className="p-5 sm:p-8 space-y-2">
        {sortedVideos.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-4">
            No videos yet.{' '}
            <button
              onClick={() => onAddVideo(section)}
              className="text-primary-400 hover:underline"
              type="button"
            >
              Add the first one.
            </button>
          </p>
        ) : (
          <DndContext
            sensors={videoSensors}
            collisionDetection={closestCenter}
            onDragEnd={(event) => onVideoDragEnd(section.id, event)}
          >
            <SortableContext
              items={sortedVideos.map((v) => v.id)}
              strategy={verticalListSortingStrategy}
            >
              {sortedVideos.map((video) => (
                <SortableVideoRow
                  key={video.id}
                  video={video}
                  onEdit={onEditVideo}
                  onDelete={onDeleteVideo}
                />
              ))}
            </SortableContext>
          </DndContext>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------
export const AdminHelp = () => {
  const queryClient = useQueryClient();

  // ---- query ----
  const { data: sections, isLoading, error } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: helpAdminService.listAllSections,
  });

  // ---- section CRUD mutations ----
  const deleteSectionMutation = useMutation({
    mutationFn: (id: number) => helpAdminService.deleteSection(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast.success('Section deleted');
    },
    onError: () => toast.error('Failed to delete section'),
  });

  const reorderSectionsMutation = useMutation<
    void,
    Error,
    number[],
    { snapshot: HelpSection[] | undefined }
  >({
    mutationFn: (orderedIds) => helpAdminService.reorderSections(orderedIds),
    onMutate: async (orderedIds) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEY });
      const snapshot = queryClient.getQueryData<HelpSection[]>(QUERY_KEY);
      queryClient.setQueryData<HelpSection[]>(QUERY_KEY, (old) => {
        if (!old) return old;
        const byId = new Map(old.map((s) => [s.id, s]));
        return orderedIds.map((id) => byId.get(id)).filter((s): s is HelpSection => s !== undefined);
      });
      return { snapshot };
    },
    onError: (_err, _vars, context) => {
      if (context?.snapshot !== undefined) {
        queryClient.setQueryData(QUERY_KEY, context.snapshot);
      }
      toast.error('Failed to save section order');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  // ---- video CRUD mutations ----
  const deleteVideoMutation = useMutation({
    mutationFn: (id: number) => helpAdminService.deleteVideo(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
      toast.success('Video deleted');
    },
    onError: () => toast.error('Failed to delete video'),
  });

  const reorderVideosMutation = useMutation<
    void,
    Error,
    { sectionId: number; orderedIds: number[] },
    { snapshot: HelpSection[] | undefined }
  >({
    mutationFn: ({ sectionId, orderedIds }) =>
      helpAdminService.reorderVideos(sectionId, orderedIds),
    onMutate: async ({ sectionId, orderedIds }) => {
      await queryClient.cancelQueries({ queryKey: QUERY_KEY });
      const snapshot = queryClient.getQueryData<HelpSection[]>(QUERY_KEY);
      queryClient.setQueryData<HelpSection[]>(QUERY_KEY, (old) => {
        if (!old) return old;
        return old.map((s) => {
          if (s.id !== sectionId) return s;
          const byId = new Map(s.videos.map((v) => [v.id, v]));
          const reorderedVideos = orderedIds
            .map((id) => byId.get(id))
            .filter((v): v is HelpVideo => v !== undefined);
          return { ...s, videos: reorderedVideos };
        });
      });
      return { snapshot };
    },
    onError: (_err, _vars, context) => {
      if (context?.snapshot !== undefined) {
        queryClient.setQueryData(QUERY_KEY, context.snapshot);
      }
      toast.error('Failed to save video order');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });

  // ---- modal state ----
  const [sectionModalOpen, setSectionModalOpen] = useState(false);
  const [editingSection, setEditingSection] = useState<HelpSection | null>(null);

  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [editingVideo, setEditingVideo] = useState<HelpVideo | null>(null);
  const [targetSectionId, setTargetSectionId] = useState<number | undefined>(undefined);

  const [deleteSectionDialog, setDeleteSectionDialog] = useState<{
    open: boolean;
    section: HelpSection | null;
  }>({ open: false, section: null });

  const [deleteVideoDialog, setDeleteVideoDialog] = useState<{
    open: boolean;
    video: HelpVideo | null;
  }>({ open: false, video: null });

  // ---- dnd-kit for sections ----
  const sectionSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const handleSectionDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id || !sections) return;

      const oldIndex = sections.findIndex((s) => s.id === active.id);
      const newIndex = sections.findIndex((s) => s.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      const reordered = arrayMove(sections, oldIndex, newIndex);
      const orderedIds = reordered.map((s) => s.id);

      reorderSectionsMutation.mutate(orderedIds);
    },
    [sections, queryClient, reorderSectionsMutation]
  );

  const handleVideoDragEnd = useCallback(
    (sectionId: number, event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id || !sections) return;

      const sectionIndex = sections.findIndex((s) => s.id === sectionId);
      if (sectionIndex === -1) return;

      const section = sections[sectionIndex];
      const sortedVideos = [...section.videos].sort((a, b) => a.sortOrder - b.sortOrder);
      const oldIndex = sortedVideos.findIndex((v) => v.id === active.id);
      const newIndex = sortedVideos.findIndex((v) => v.id === over.id);
      if (oldIndex === -1 || newIndex === -1) return;

      const reorderedVideos = arrayMove(sortedVideos, oldIndex, newIndex);
      const orderedIds = reorderedVideos.map((v) => v.id);

      reorderVideosMutation.mutate({ sectionId, orderedIds });
    },
    [sections, queryClient, reorderVideosMutation]
  );

  // ---- handlers ----
  const handleAddSection = () => {
    setEditingSection(null);
    setSectionModalOpen(true);
  };

  const handleEditSection = (s: HelpSection) => {
    setEditingSection(s);
    setSectionModalOpen(true);
  };

  const handleDeleteSection = (s: HelpSection) => {
    setDeleteSectionDialog({ open: true, section: s });
  };

  const handleAddVideo = (s: HelpSection) => {
    setEditingVideo(null);
    setTargetSectionId(s.id);
    setVideoModalOpen(true);
  };

  const handleEditVideo = (v: HelpVideo) => {
    setEditingVideo(v);
    setTargetSectionId(v.sectionId);
    setVideoModalOpen(true);
  };

  const handleDeleteVideo = (v: HelpVideo) => {
    setDeleteVideoDialog({ open: true, video: v });
  };

  // ---- render ----
  if (error) {
    return (
      <div className="bg-red-50 text-red-600 p-4 rounded-lg">
        Failed to load help content. Please refresh the page.
      </div>
    );
  }

  const sortedSections = sections
    ? [...sections].sort((a, b) => a.sortOrder - b.sortOrder)
    : [];

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Help Videos</h1>
          <p className="text-gray-500 mt-1">
            Manage tutorials and playlists for the Help page
          </p>
        </div>
        <Button onClick={handleAddSection}>
          <i className={`hm hm-${ICONS.PLUS} mr-1.5`} aria-hidden="true" />
          Add Section
        </Button>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 flex justify-center">
          <LoadingSpinner />
        </div>
      ) : sortedSections.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <i
            className={`hm hm-${ICONS.PLAY} text-4xl text-gray-300 block mb-3`}
            aria-hidden="true"
          />
          <p className="text-gray-500 text-sm">No sections yet. Create your first section to get started.</p>
        </div>
      ) : (
        <DndContext
          sensors={sectionSensors}
          collisionDetection={closestCenter}
          onDragEnd={handleSectionDragEnd}
        >
          <SortableContext
            items={sortedSections.map((s) => s.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-4">
              {sortedSections.map((section) => (
                <SortableSectionCard
                  key={section.id}
                  section={section}
                  onEditSection={handleEditSection}
                  onDeleteSection={handleDeleteSection}
                  onAddVideo={handleAddVideo}
                  onEditVideo={handleEditVideo}
                  onDeleteVideo={handleDeleteVideo}
                  onVideoDragEnd={handleVideoDragEnd}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Section modal */}
      {sectionModalOpen && (
        <SectionModal
          open={sectionModalOpen}
          onClose={() => setSectionModalOpen(false)}
          initialData={editingSection}
        />
      )}

      {/* Video modal */}
      {videoModalOpen && (
        <VideoModal
          open={videoModalOpen}
          onClose={() => setVideoModalOpen(false)}
          sections={sections ?? []}
          initialData={editingVideo}
          defaultSectionId={targetSectionId}
        />
      )}

      {/* Delete section confirm */}
      <ConfirmDialog
        open={deleteSectionDialog.open}
        onOpenChange={(open) => !open && setDeleteSectionDialog({ open: false, section: null })}
        title="Delete Section"
        description={`Delete "${deleteSectionDialog.section?.title}"? This will also delete all ${deleteSectionDialog.section?.videos.length ?? 0} video(s) in it. This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        onConfirm={() => {
          if (deleteSectionDialog.section) {
            deleteSectionMutation.mutate(deleteSectionDialog.section.id);
          }
          setDeleteSectionDialog({ open: false, section: null });
        }}
        loading={deleteSectionMutation.isPending}
      />

      {/* Delete video confirm */}
      <ConfirmDialog
        open={deleteVideoDialog.open}
        onOpenChange={(open) => !open && setDeleteVideoDialog({ open: false, video: null })}
        title="Delete Video"
        description={`Delete "${deleteVideoDialog.video?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        onConfirm={() => {
          if (deleteVideoDialog.video) {
            deleteVideoMutation.mutate(deleteVideoDialog.video.id);
          }
          setDeleteVideoDialog({ open: false, video: null });
        }}
        loading={deleteVideoMutation.isPending}
      />
    </div>
  );
};

export default AdminHelp;
